import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import adminApi, { setAdminAccessToken } from '../../shared/api/AxiosAdmin';

type AdminCtx = {
  adminToken: string | null;
  adminOk: boolean;
  loading: boolean;
  logout: () => void;
  refresh: () => Promise<void>;
};

const C = createContext<AdminCtx | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [adminToken, setAdminToken] = useState<string | null>(
    localStorage.getItem('unyAdminToken')
  );
  const [adminOk, setAdminOk] = useState(false);
  const [loading, setLoading] = useState(true);

  const applyToken = (t: string | null) => {
    // This makes your axios interceptor attach Authorization automatically
    setAdminAccessToken(t);
    setAdminToken(t);
  };

  const logout = () => {
    localStorage.removeItem('unyAdminToken');
    applyToken(null);
    setAdminOk(false);
  };

  const refresh = async () => {
    const t = localStorage.getItem('unyAdminToken');
    applyToken(t);

    if (!t) {
      setAdminOk(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // No manual headers needed — interceptor uses the in-memory token
      const res = await adminApi.get('/admin/auth/whoami');
      setAdminOk(!!res.data?.ok && !!res.data?.admin);
    } catch {
      setAdminOk(false);
      localStorage.removeItem('unyAdminToken');
      applyToken(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({ adminToken, adminOk, loading, logout, refresh }),
    [adminToken, adminOk, loading]
  );

  return <C.Provider value={value}>{children}</C.Provider>;
};

export const useAdminAuth = () => {
  const ctx = useContext(C);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
};
